import { uniq } from 'ramda';
import { generate } from 'short-uuid';

import {
  OUTBOUND,
  INBOUND,
  BOTH,
  ASSISTANCE_ANIMAL_ADDITIONAL_DETAILS_KEY,
  PASSENGER_DISABILITY_ADDITIONAL_DETAILS_KEY
} from 'constants/booking';

import { actionTypes } from './AddAssistanceForm.actions';
import { ASSISTANCE_KEYS, PASSENGER_DISABILITY_KEYS } from 'constants/assistance';

const emptyPassengerItem = (isReturn, direction, paxIdHetergoneous = '') => ({
  id: generate(),
  passengerId: paxIdHetergoneous,
  comment: '',
  passengerDisability: PASSENGER_DISABILITY_KEYS.NONE,
  numberOfOutboundItems: [BOTH, OUTBOUND].includes(direction) ? 1 : 0,
  numberOfInboundItems: [BOTH, INBOUND].includes(direction) ? 1 : 0
});

const normalizePassengerDisability = (passengerDisability) =>
  passengerDisability === PASSENGER_DISABILITY_KEYS.NONE ? '' : passengerDisability || '';

const isDogAssistance = (extraId) => extraId === ASSISTANCE_KEYS.GUIDE_DOG_ASSIST;

const hasSameIdentity = (a, b) =>
  a.extraId === b.extraId &&
  a.passengerId === b.passengerId &&
  a.direction === b.direction &&
  (isDogAssistance(a.extraId) || a.comment === b.comment);

const hasSameSecondaryDetails = (a, b) =>
  a.comment === b.comment &&
  a.assistanceAnimal === b.assistanceAnimal &&
  normalizePassengerDisability(a.passengerDisability) === normalizePassengerDisability(b.passengerDisability);

const isUnsettingSecondaryDetails = (updatedExtra, initialExtra) =>
  [
    [normalizePassengerDisability(initialExtra.passengerDisability), normalizePassengerDisability(updatedExtra.passengerDisability)],
    [initialExtra.assistanceAnimal, updatedExtra.assistanceAnimal],
    [initialExtra.comment, updatedExtra.comment]
  ].some(([initialValue, updatedValue]) => Boolean(initialValue) && !updatedValue);

const buildIdentityKey = ({ extraId, passengerId, direction, comment }) =>
  [extraId, passengerId, direction, isDogAssistance(extraId) ? '' : comment].join('|');

const buildRemovedDetails = (extras) =>
  extras.flatMap(({ itemRef, additionalDetails = [] }) =>
    additionalDetails.map(({ key, value }) => ({ itemRefId: parseInt(itemRef, 10), key, value }))
  );

const countByIdentity = (extras) =>
  extras.reduce((counts, extra) => {
    const key = buildIdentityKey(extra);

    return { ...counts, [key]: (counts[key] || 0) + 1 };
  }, {});

const checkAssistanceForIncompleteItems = (items) => Object.values(items).some((item) => item.isIncomplete);

const checkForIncompleteItems = (items, assistanceType) => {
  if (items.length === 0) {
    return false;
  }

  return items.some(
    ({ passengerId, numberOfOutboundItems, numberOfInboundItems, assistanceAnimal }) =>
      !passengerId ||
      (numberOfOutboundItems < 1 && numberOfInboundItems < 1) ||
      (assistanceType === ASSISTANCE_KEYS.GUIDE_DOG_ASSIST && !assistanceAnimal)
  );
};

const formatAssistance = (assistance) => {
  const totalItems = [];
  Object.keys(assistance).forEach((assistanceId) => {
    const { items } = assistance[assistanceId];
    items.forEach(
      ({ passengerId, comment, numberOfOutboundItems = 0, numberOfInboundItems = 0, assistanceAnimal, passengerDisability }) => {
        for (let i = 0; i < numberOfOutboundItems; i += 1) {
          totalItems.push({
            extraId: assistanceId,
            passengerId,
            comment,
            direction: OUTBOUND,
            assistanceAnimal,
            passengerDisability
          });
        }
        for (let i = 0; i < numberOfInboundItems; i += 1) {
          totalItems.push({
            extraId: assistanceId,
            passengerId,
            comment,
            direction: INBOUND,
            assistanceAnimal,
            passengerDisability
          });
        }
      }
    );
  });

  return totalItems;
};

const formatAssistanceToPayload = ({ assistance, outboundExtras, inboundExtras }) => {
  const initialOutboundExtras = outboundExtras
    .filter((e) => !e.cancelled)
    .map((e) => ({
      itemRef: e.itemRef,
      extraId: e.tariffDetails?.tariffCode,
      direction: OUTBOUND,
      passengerId: e.passengerId,
      additionalDetails: e.additionalDetails || [],
      comment: e.additionalDetails?.find((d) => d.key === 'MAAS')?.value || '',
      assistanceAnimal: e.additionalDetails?.find((d) => d.key === ASSISTANCE_ANIMAL_ADDITIONAL_DETAILS_KEY)?.value || '',
      passengerDisability: e.additionalDetails?.find((d) => d.key === PASSENGER_DISABILITY_ADDITIONAL_DETAILS_KEY)?.value || ''
    }));
  const initialInboundExtras = inboundExtras
    .filter((e) => !e.cancelled)
    .map((e) => ({
      itemRef: e.itemRef,
      extraId: e.tariffDetails?.tariffCode,
      direction: INBOUND,
      passengerId: e.passengerId,
      additionalDetails: e.additionalDetails || [],
      comment: e.additionalDetails?.find((d) => d.key === 'MAAS')?.value || '',
      assistanceAnimal: e.additionalDetails?.find((d) => d.key === ASSISTANCE_ANIMAL_ADDITIONAL_DETAILS_KEY)?.value || '',
      passengerDisability: e.additionalDetails?.find((d) => d.key === PASSENGER_DISABILITY_ADDITIONAL_DETAILS_KEY)?.value || ''
    }));
  const initialExtras = initialOutboundExtras.concat(initialInboundExtras).filter((e) => assistance[e.extraId]);

  const updatedExtras = formatAssistance(assistance);

  const unmatchedInitialExtras = [...initialExtras];
  const unmatchedUpdatedExtras = [...updatedExtras];
  const extrasToUpdate = [];

  const matchExtras = (isMatch) => {
    for (let i = unmatchedInitialExtras.length - 1; i >= 0; i -= 1) {
      const extra = unmatchedInitialExtras[i];
      const updatedIndex = unmatchedUpdatedExtras.findIndex((e) => isMatch(e, extra));

      if (updatedIndex !== -1) {
        const [updatedExtra] = unmatchedUpdatedExtras.splice(updatedIndex, 1);
        unmatchedInitialExtras.splice(i, 1);

        if (!hasSameSecondaryDetails(updatedExtra, extra)) {
          extrasToUpdate.push({
            itemRef: extra.itemRef,
            id: extra.extraId,
            comment: updatedExtra.comment,
            assistanceAnimal: updatedExtra.assistanceAnimal,
            passengerDisability: updatedExtra.passengerDisability,
            initialDetails: {
              comment: extra.comment,
              assistanceAnimal: extra.assistanceAnimal,
              passengerDisability: extra.passengerDisability
            }
          });
        }
      }
    }
  };

  matchExtras((e, extra) => hasSameIdentity(e, extra) && hasSameSecondaryDetails(e, extra));

  const initialCountsByIdentity = countByIdentity(unmatchedInitialExtras);
  const updatedCountsByIdentity = countByIdentity(unmatchedUpdatedExtras);

  matchExtras(
    (e, extra) =>
      hasSameIdentity(e, extra) &&
      !isUnsettingSecondaryDetails(e, extra) &&
      initialCountsByIdentity[buildIdentityKey(extra)] === updatedCountsByIdentity[buildIdentityKey(e)]
  );

  const payload = {
    add: uniq(unmatchedUpdatedExtras.map((e) => e.extraId)).map((id) => ({
      id,
      items: unmatchedUpdatedExtras
        .filter((e) => e.extraId === id)
        .map((e) => ({
          passengerId: e.passengerId,
          comment: e.comment,
          direction: e.direction,
          assistanceAnimal: e.assistanceAnimal,
          passengerDisability: e.passengerDisability
        }))
    })),
    update: extrasToUpdate,
    remove: unmatchedInitialExtras.map((e) => e.itemRef),
    removedDetails: buildRemovedDetails(unmatchedInitialExtras)
  };

  return payload;
};

export const getInitialState = (outboundExtras = [], inboundExtras = [], options, passengers = [], isReturn, paxIdHeterogenous = '') => {
  const paxs = paxIdHeterogenous ? passengers.filter((p) => p.id === paxIdHeterogenous) : passengers;
  const outExtras = paxIdHeterogenous ? outboundExtras.filter((oe) => oe.passengerId === paxIdHeterogenous) : outboundExtras;
  const inExtras = paxIdHeterogenous ? inboundExtras.filter((oe) => oe.passengerId === paxIdHeterogenous) : inboundExtras;
  const targetShape = {
    passengerCount: paxs.length,
    passengers: paxs.map(({ id, type }) => ({
      id,
      type
    })),
    assistance: {},
    isAssistanceIncomplete: false,
    formPayload: {
      add: [],
      update: [],
      remove: [],
      removedDetails: []
    },
    isReturn,
    outboundExtras: outExtras,
    inboundExtras: inExtras,
    ...(paxIdHeterogenous !== '' && { paxIdHeterogenous })
  };

  const defaultAssistance = {
    isVisible: false,
    isIncomplete: false,
    isAddPassengersDisabled: false,
    items: []
  };

  return options.reduce((acc, item) => {
    const existingOutboundExtras = paxIdHeterogenous
      ? outboundExtras.filter((e) => !e.cancelled && e.tariffDetails?.tariffCode === item.id && e.passengerId === paxIdHeterogenous)
      : outboundExtras.filter((e) => !e.cancelled && e.tariffDetails?.tariffCode === item.id);
    const existingInboundExtras = paxIdHeterogenous
      ? inboundExtras.filter((e) => !e.cancelled && e.tariffDetails?.tariffCode === item.id && e.passengerId === paxIdHeterogenous)
      : inboundExtras.filter((e) => !e.cancelled && e.tariffDetails?.tariffCode === item.id);
    const existingExtras = existingOutboundExtras.concat(existingInboundExtras);
    const existingExtrasPerPax = existingExtras.reduce((extrasPerPax, existingExtra) => {
      if (!extrasPerPax[existingExtra.passengerId]) {
        // eslint-disable-next-line no-param-reassign
        extrasPerPax[existingExtra.passengerId] = {
          outbound: [],
          inbound: []
        };
      }
      const isOutbound = existingOutboundExtras.find((e) => e === existingExtra);
      extrasPerPax[existingExtra.passengerId][isOutbound ? 'outbound' : 'inbound'].push(existingExtra);

      return extrasPerPax;
    }, {});

    const items = existingExtras.length
      ? Object.keys(existingExtrasPerPax).map((paxId) => ({
          id: generate(),
          passengerId: paxId,
          isExisting: true,
          numberOfOutboundItems: existingExtrasPerPax[paxId].outbound.length || 0,
          numberOfInboundItems: existingExtrasPerPax[paxId].inbound.length || 0,
          comment:
            (existingExtrasPerPax[paxId].outbound[0] || existingExtrasPerPax[paxId].inbound[0]).additionalDetails?.find(
              (d) => d.key === 'MAAS'
            )?.value || '',
          assistanceAnimal:
            (existingExtrasPerPax[paxId].outbound[0] || existingExtrasPerPax[paxId].inbound[0]).additionalDetails?.find(
              (d) => d.key === ASSISTANCE_ANIMAL_ADDITIONAL_DETAILS_KEY
            )?.value || '',
          passengerDisability:
            (existingExtrasPerPax[paxId].outbound[0] || existingExtrasPerPax[paxId].inbound[0]).additionalDetails?.find(
              (d) => d.key === PASSENGER_DISABILITY_ADDITIONAL_DETAILS_KEY
            )?.value || ''
        }))
      : [];

    return {
      ...acc,
      assistance: {
        ...acc.assistance,
        [item.id]: {
          ...defaultAssistance,
          isExtra: item.passengerTypes != null,
          items,
          isAddPassengersDisabled: items.length === passengers.length,
          direction: item.direction
        }
      }
    };
  }, targetShape);
};

export const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.ON_TOGGLE: {
      const currentItems = state.assistance[action.payload.key].items;
      const { isExtra, direction } = state.assistance[action.payload.key];
      const hasItems = currentItems.length > 0;
      const items = hasItems ? currentItems : [emptyPassengerItem(state.isReturn, direction)];
      const isIncomplete = checkForIncompleteItems(items, action.payload.key);
      const assistance = {
        ...state.assistance,
        [action.payload.key]: {
          isAddPassengersDisabled: items.length === state.passengerCount,
          isVisible: !state.assistance[action.payload.key].isVisible,
          isIncomplete,
          items,
          isExtra,
          direction
        }
      };
      const isAssistanceIncomplete = checkAssistanceForIncompleteItems(assistance);
      const formPayload = formatAssistanceToPayload({
        ...state,
        assistance
      });

      return {
        ...state,
        isAssistanceIncomplete,
        assistance,
        formPayload
      };
    }

    case actionTypes.ON_REMOVE_ALL: {
      const isIncomplete = false;
      const { isExtra, direction } = state.assistance[action.payload.key];

      const assistance = {
        ...state.assistance,
        [action.payload.key]: {
          isAddPassengersDisabled: false,
          isVisible: false,
          isIncomplete,
          items: [],
          direction,
          isExtra
        }
      };

      const isAssistanceIncomplete = checkAssistanceForIncompleteItems(assistance);
      const formPayload = formatAssistanceToPayload({
        ...state,
        assistance
      });
      return {
        ...state,
        isAssistanceIncomplete,
        assistance,
        formPayload
      };
    }

    case actionTypes.ON_REMOVE: {
      const currentItems = state.assistance[action.payload.key].items;
      const { isExtra, direction } = state.assistance[action.payload.key];
      const items = currentItems.filter((item) => item.id !== action.payload.item.id);
      const hasItems = items.length > 0;
      const isIncomplete = checkForIncompleteItems(items, action.payload.key);

      const assistance = {
        ...state.assistance,
        [action.payload.key]: {
          isAddPassengersDisabled: false,
          isVisible: hasItems,
          isIncomplete,
          items,
          isExtra,
          direction
        }
      };

      const isAssistanceIncomplete = checkAssistanceForIncompleteItems(assistance);
      const formPayload = formatAssistanceToPayload({
        ...state,
        assistance
      });

      return {
        ...state,
        isAssistanceIncomplete,
        assistance,
        formPayload
      };
    }

    case actionTypes.ON_ADD: {
      const currentItems = state.assistance[action.payload.key].items;
      const { isExtra, direction } = state.assistance[action.payload.key];
      const items = [...currentItems, emptyPassengerItem(state.isReturn, direction, state.paxIdHeterogenous)];
      const isAddPassengersDisabled = items.length === state.passengerCount;

      const assistance = {
        ...state.assistance,
        [action.payload.key]: {
          isAddPassengersDisabled,
          items,
          isIncomplete: checkForIncompleteItems(items, action.payload.key),
          isVisible: true,
          isExtra,
          direction
        }
      };
      const isAssistanceIncomplete = checkAssistanceForIncompleteItems(assistance);
      const formPayload = formatAssistanceToPayload({
        ...state,
        assistance
      });

      return {
        ...state,
        assistance,
        isAssistanceIncomplete,
        formPayload
      };
    }

    case actionTypes.ON_EDIT: {
      const currentItems = state.assistance[action.payload.key].items;
      const { isExtra, direction } = state.assistance[action.payload.key];
      const itemBeingEdited = action.payload.item;

      const items = currentItems.map((item) => {
        const isItemBeingEdited = item.id === itemBeingEdited.id;

        return isItemBeingEdited ? itemBeingEdited : item;
      });

      const isAddPassengersDisabled = items.length === state.passengerCount;
      const isIncomplete = checkForIncompleteItems(items, action.payload.key);
      const assistance = {
        ...state.assistance,
        [action.payload.key]: {
          isIncomplete,
          isAddPassengersDisabled,
          items,
          isVisible: true,
          isExtra,
          direction
        }
      };

      const isAssistanceIncomplete = checkAssistanceForIncompleteItems(assistance);
      const formPayload = formatAssistanceToPayload({
        ...state,
        assistance
      });

      return {
        ...state,
        isAssistanceIncomplete,
        assistance,
        formPayload
      };
    }

    default: {
      return state;
    }
  }
};
